import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, ArrowLeft, Check, Download, Loader2, GraduationCap, Clock, Award, BookOpen, PlayCircle, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [syllabus, setSyllabus] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { add } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      if (r.data.type === "course") api.get(`/courses/${id}/syllabus`).then((s) => setSyllabus(s.data)).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <p className="text-slate-500">Product not found.</p>
      <Button asChild className="mt-4 bg-blue-900 text-white"><Link to="/store">Back to store</Link></Button>
    </div>
  );
  if (!product) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const isCourse = product.type === "course";
  const buyNow = () => { add(product); navigate("/store"); toast.success("Added — open cart to checkout"); };
  const scrollTo = (el) => document.getElementById(el)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/store" className="inline-flex items-center gap-1.5 text-sm text-blue-800 dark:text-blue-300 mb-6 hover:gap-2.5 transition-all"><ArrowLeft className="w-4 h-4" /> Back to store</Link>
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="relative">
          <img src={isCourse && syllabus?.sample?.image_url ? syllabus.sample.image_url : product.image} alt={product.name} className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]" data-testid="product-detail-image" />
          {product.badge && <span className="absolute top-4 left-4 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">{product.badge}</span>}
          {isCourse && syllabus && (
            <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
              <span className="bg-blue-950/90 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-teal-300" /> {syllabus.total_lessons} lessons</span>
              {syllabus.ce_hours ? <span className="bg-teal-500/95 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> {syllabus.ce_hours} CE hours</span> : null}
              <span className="bg-blue-950/90 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5 text-teal-300" /> Certificate</span>
            </div>
          )}
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-amber-700 font-semibold">{product.category}</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950 dark:text-blue-50 mt-2 leading-tight">{product.name}</h1>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">{product.description}</p>
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            {isCourse ? <><GraduationCap className="w-4 h-4 text-blue-700" /> Narrated lessons + workbook + certificate on completion</> : <><Download className="w-4 h-4 text-blue-700" /> Instant digital download after purchase</>}
          </div>
          <div className="mt-8 flex items-end gap-4">
            <span className="font-display font-bold text-4xl text-blue-900 dark:text-blue-300" data-testid="product-detail-price">${product.price.toFixed(2)}</span>
            <span className="text-sm text-slate-400 mb-1">one-time</span>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button data-testid="product-detail-add-button" onClick={() => { add(product); toast.success("Added to cart"); }} className="h-13 px-7 bg-blue-900 hover:bg-blue-950 text-white gap-2"><ShoppingCart className="w-5 h-5" /> Add to Cart</Button>
            <Button data-testid="product-detail-buy-button" onClick={buyNow} variant="outline" className="h-13 px-7 border-amber-500 text-amber-700 hover:bg-amber-50">Buy Now</Button>
            {isCourse && syllabus?.sample && <Button data-testid="preview-sample-button" onClick={() => scrollTo("sample-lesson")} variant="ghost" className="h-13 px-5 text-blue-800 gap-2"><PlayCircle className="w-5 h-5" /> Preview a lesson</Button>}
          </div>
          <ul className="mt-8 space-y-2.5">
            {(isCourse
              ? [`${syllabus?.total_lessons || ""} guided narrated lessons across ${syllabus?.modules?.length || ""} modules`, syllabus?.ce_hours ? `Approx. ${syllabus.ce_hours} continuing-education (CE) hours` : "Continuing-education content", "Downloadable workbook & printable certificate", "Created by Ohio home-care professionals"]
              : ["Lifetime access to your download", "Created by Ohio home-care professionals", "PASSPORT / Medicaid Waiver aligned content"]
            ).filter(Boolean).map((f) => (
              <li key={f} className="flex items-center gap-2.5 text-sm text-slate-700 dark:text-slate-300"><Check className="w-4 h-4 text-blue-600" /> {f}</li>
            ))}
          </ul>
        </div>
      </div>

      {isCourse && syllabus && (
        <div className="mt-16 grid lg:grid-cols-[1fr_360px] gap-10">
          <div>
            <div className="flex items-center gap-2 mb-5"><Layers className="w-5 h-5 text-teal-600" /><h2 className="font-display text-2xl font-bold text-blue-950">Course curriculum</h2></div>
            <div className="space-y-6" data-testid="course-curriculum">
              {syllabus.modules.map((mod, mi) => (
                <div key={mi} className="bg-white dark:bg-slate-900 rounded-2xl border border-blue-900/10 shadow-sm overflow-hidden">
                  <div className="bg-blue-950 text-white px-5 py-3 flex items-center justify-between">
                    <span className="font-semibold">{mod.name}</span>
                    <span className="text-xs text-blue-200">{mod.lessons.length} {mod.lessons.length === 1 ? "lesson" : "lessons"}</span>
                  </div>
                  <div className="divide-y divide-blue-900/5">
                    {mod.lessons.map((l, li) => (
                      <div key={li} className="px-5 py-4" data-testid={`syllabus-lesson-${mi}-${li}`}>
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium text-blue-950 dark:text-blue-50 flex items-center gap-2.5"><PlayCircle className="w-4 h-4 text-teal-600 shrink-0" /> {l.title}</span>
                          {l.duration && <span className="text-xs text-slate-400 shrink-0 flex items-center gap-1"><Clock className="w-3 h-3" /> {l.duration}</span>}
                        </div>
                        {l.objectives?.length > 0 && (
                          <ul className="mt-2 ml-6.5 pl-0 space-y-1">
                            {l.objectives.slice(0, 3).map((o, oi) => <li key={oi} className="text-sm text-slate-500 flex items-start gap-2"><Check className="w-3.5 h-3.5 text-teal-500 shrink-0 mt-0.5" /> {o}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {syllabus.sample && (
              <div id="sample-lesson" className="mt-12 scroll-mt-24">
                <div className="flex items-center gap-2 mb-4"><PlayCircle className="w-5 h-5 text-teal-600" /><h2 className="font-display text-2xl font-bold text-blue-950">Free sample lesson</h2></div>
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-teal-200 shadow-sm overflow-hidden" data-testid="sample-lesson-card">
                  {syllabus.sample.image_url && <img src={syllabus.sample.image_url} alt={syllabus.sample.title} className="w-full aspect-video object-cover" />}
                  <div className="p-6">
                    <span className="text-[11px] uppercase tracking-wide font-bold text-teal-700 bg-teal-50 px-2.5 py-1 rounded-full">Preview</span>
                    <h3 className="font-display text-xl font-semibold text-blue-950 mt-3">{syllabus.sample.title}</h3>
                    {syllabus.sample.audio_url && (
                      <div className="mt-4 bg-blue-950 rounded-xl p-3.5 flex items-center gap-3">
                        <span className="text-xs font-semibold text-teal-300 shrink-0 pl-1">▶ Narration</span>
                        <audio controls className="w-full h-9" data-testid="sample-lesson-audio"><source src={syllabus.sample.audio_url} type="audio/mpeg" /></audio>
                      </div>
                    )}
                    {syllabus.sample.body && <p className="mt-4 text-slate-600 dark:text-slate-300 leading-relaxed">{syllabus.sample.body}</p>}
                  </div>
                </div>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="bg-gradient-to-br from-blue-950 to-blue-900 text-white rounded-2xl p-6 shadow-lg">
              <p className="text-teal-300 text-xs uppercase tracking-wide font-semibold">Enroll today</p>
              <p className="font-display text-3xl font-bold mt-2">${product.price.toFixed(2)}</p>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center gap-2.5"><BookOpen className="w-4 h-4 text-teal-300" /> {syllabus.total_lessons} narrated lessons</div>
                {syllabus.ce_hours ? <div className="flex items-center gap-2.5"><Award className="w-4 h-4 text-teal-300" /> {syllabus.ce_hours} CE hours</div> : null}
                <div className="flex items-center gap-2.5"><GraduationCap className="w-4 h-4 text-teal-300" /> Quiz + printable certificate</div>
                <div className="flex items-center gap-2.5"><Download className="w-4 h-4 text-teal-300" /> Downloadable workbook</div>
              </div>
              <Button data-testid="sidebar-enroll-button" onClick={() => { add(product); toast.success("Added to cart"); }} className="mt-6 w-full h-12 bg-teal-500 hover:bg-teal-600 text-white gap-2"><ShoppingCart className="w-5 h-5" /> Enroll now</Button>
              <p className="mt-3 text-center text-[11px] text-blue-200">Instant access after checkout</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
